import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import supabase from '../utils/supabaseClient';

/**
 * Auth Callback Handler Component
 * Processes the authentication callback from Google OAuth
 * Follows human-in-the-loop principles by providing clear feedback during the process
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState(null);
  
  useEffect(() => {
    // Handle the OAuth callback
    const handleAuthCallback = async () => {
      try {
        // Check if we're redirected from production environment
        const isFromProduction = window.location.href.includes('wyspur-567eed076d61.herokuapp.com');
        
        // The hash contains the token information
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        
        // Handle case where we're redirected from production with a token
        if (isFromProduction && accessToken) {
          console.log('Detected redirect from production environment with token');
          // Extract user data from token if possible for debugging
          try {
            const userData = JSON.parse(atob(accessToken.split('.')[1]));
            setUserData(userData);
            console.log('User successfully authenticated:', userData.email);
          } catch (e) {
            console.error('Error parsing token:', e);
          }
          
          // Set the session with the token from the URL
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: hashParams.get('refresh_token'),
          });
          
          if (error) throw error;
          
          // Redirect to main app after short delay
          setTimeout(() => navigate('/'), 1500);
          return;
        }
        
        // Standard local development flow
        if (!accessToken) {
          // If no token is found, try to process the authentication via Supabase
          const { data, error } = await supabase.auth.getSession();
          
          if (error) throw error;
          
          if (data?.session) {
            // Successfully authenticated
            console.log('Authentication successful via session');
            // Redirect to main app after short delay
            setTimeout(() => navigate('/'), 1500);
            return;
          }
        } else {
          // Token found in URL, manually set the session
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: hashParams.get('refresh_token'),
          });
          
          if (error) throw error;
          
          // Redirect to main app after short delay
          setTimeout(() => navigate('/'), 1500);
          return;
        }
      } catch (err) {
        console.error('Error during auth callback:', err);
        setError(err.message || 'Authentication failed. Please try again.');
      }
    };

    handleAuthCallback();
  }, [navigate]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        gap: 3,
        p: 3,
      }}
    >
      {error ? (
        <Alert severity="error" sx={{ width: '100%', maxWidth: 500 }}>
          {error}
        </Alert>
      ) : (
        <>
          <CircularProgress size={60} />
          <Typography variant="h6">
            Completing authentication...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You'll be redirected to the application in a moment.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AuthCallback;
